import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Info, Database, Clock, Shield } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * DataCredibilityBadge - Shows data source and confidence in institutional format
 */
export const DataCredibilityBadge = ({ source, confidence, compact = false }) => {
  const confidenceColors = {
    'HIGH': 'bg-green-600/20 text-green-400 border-green-600/30',
    'MODERATE': 'bg-gold-600/20 text-gold-400 border-gold-600/30',
    'LIMITED': 'bg-red-600/20 text-red-400 border-red-600/30'
  };
  
  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge variant="outline" className={`text-[10px] ${confidenceColors[confidence] || confidenceColors.MODERATE}`}>
              {source}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Source: {source}</p>
            <p className="text-xs">Confidence: {confidence}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Database className="w-3 h-3" />
      <span>{source}</span>
      <Badge variant="outline" className={`text-[10px] ${confidenceColors[confidence] || confidenceColors.MODERATE}`}>
        {confidence}
      </Badge>
    </div>
  );
};

/**
 * CredibleMetric - Displays a metric value with full credibility metadata
 * Institutional format: not raw JSON, but clean visual hierarchy
 */
export const CredibleMetric = ({ 
  label, 
  value, 
  unit = '',
  source,
  confidence,
  sampleSize,
  dataWindow,
  lastUpdated,
  size = 'md' // sm, md, lg
}) => {
  // Handle both raw values and credibility-wrapped values
  const metricValue = typeof value === 'object' && value?.value !== undefined ? value.value : value;
  const metricSource = source || (typeof value === 'object' ? value.source : null);
  const metricConfidence = confidence || (typeof value === 'object' ? value.confidence : null);
  const metricSampleSize = sampleSize || (typeof value === 'object' ? value.sample_size : null);
  const metricDataWindow = dataWindow || (typeof value === 'object' ? value.data_window : null);
  
  const sizes = {
    sm: { value: 'text-lg', label: 'text-[10px]' },
    md: { value: 'text-xl', label: 'text-xs' },
    lg: { value: 'text-3xl', label: 'text-sm' }
  };
  
  const confidenceColors = {
    'HIGH': 'border-green-600/30',
    'MODERATE': 'border-gold-600/30',
    'LIMITED': 'border-red-600/30'
  };
  
  return (
    <div className={`p-3 rounded border ${confidenceColors[metricConfidence] || 'border-white/10'} bg-secondary/20`}>
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        <span className={`uppercase tracking-widest ${sizes[size].label}`}>{label}</span>
      </div>
      
      <div className={`font-display text-primary ${sizes[size].value}`}>
        {metricValue}{unit}
      </div>
      
      {/* Credibility footer */}
      {metricSource && (
        <div className="mt-2 pt-2 border-t border-white/5 space-y-1">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <span>Source: {metricSource}</span>
            {metricConfidence && (
              <Badge variant="outline" className={`text-[9px] ${
                metricConfidence === 'HIGH' ? 'border-green-600/30 text-green-400' :
                metricConfidence === 'MODERATE' ? 'border-gold-600/30 text-gold-400' :
                'border-red-600/30 text-red-400'
              }`}>
                {metricConfidence}
              </Badge>
            )}
          </div>
          {metricSampleSize && (
            <div className="text-[10px] text-muted-foreground/70">
              Sample: {metricSampleSize} transactions
            </div>
          )}
          {metricDataWindow && (
            <div className="text-[10px] text-muted-foreground/70">
              Data: {metricDataWindow}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * ZoneConfidenceCard - Displays zone-level confidence index
 */
export const ZoneConfidenceCard = ({ zoneData }) => {
  if (!zoneData) return null;
  
  const confidenceValue = typeof zoneData.confidence_index === 'object' 
    ? zoneData.confidence_index.value 
    : zoneData.confidence_index;
  
  const getConfidenceColor = (score) => {
    if (score >= 75) return 'text-green-400';
    if (score >= 65) return 'text-gold-400';
    return 'text-orange-400';
  };
  
  const getMomentumColor = (momentum) => {
    if (momentum === 'Strengthening') return 'text-green-400';
    if (momentum === 'Stable') return 'text-blue-400';
    return 'text-orange-400';
  };
  
  return (
    <div className="p-4 rounded border border-white/10 bg-secondary/20">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-xs uppercase tracking-widest text-muted-foreground">Zone Confidence Index</h4>
          <p className="text-sm font-medium mt-1">{zoneData.zone}</p>
        </div>
        <div className="text-right">
          <div className={`text-3xl font-display ${getConfidenceColor(confidenceValue)}`}>
            {confidenceValue}%
          </div>
          <div className={`text-xs ${getMomentumColor(zoneData.momentum)}`}>
            {zoneData.momentum}
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="p-2 rounded bg-secondary/30">
          <p className="text-[10px] text-muted-foreground uppercase">Liquidity</p>
          <p className="text-sm font-medium">{zoneData.liquidity_depth}</p>
        </div>
        <div className="p-2 rounded bg-secondary/30">
          <p className="text-[10px] text-muted-foreground uppercase">Stability</p>
          <p className="text-sm font-medium">{zoneData.capital_stability}</p>
        </div>
        <div className="p-2 rounded bg-secondary/30">
          <p className="text-[10px] text-muted-foreground uppercase">Volatility</p>
          <p className="text-sm font-medium">{zoneData.volatility}</p>
        </div>
      </div>
      
      {/* Credibility footer */}
      <div className="mt-3 pt-2 border-t border-white/5 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>Source: DLD</span>
        <Badge variant="outline" className="text-[9px] border-green-600/30 text-green-400">
          HIGH
        </Badge>
      </div>
    </div>
  );
};

/**
 * PriceElasticityCard - Displays negotiation elasticity band (verified buyers only)
 */
export const PriceElasticityCard = ({ elasticityData }) => {
  if (!elasticityData) return null;
  
  // Helper to extract value from credibility-wrapped fields
  const getValue = (field) => {
    if (field && typeof field === 'object' && 'value' in field) {
      return field.value;
    }
    return field;
  };
  
  const strengthColors = {
    'Very High': 'text-green-400',
    'High': 'text-green-400',
    'Moderate': 'text-gold-400',
    'Limited': 'text-orange-400'
  };
  
  const modelConfidence = getValue(elasticityData.model_confidence);
  const narrative = getValue(elasticityData.narrative);
  const elasticityStrength = getValue(elasticityData.elasticity_strength);
  
  return (
    <div className="p-4 rounded border border-primary/30 bg-primary/5">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-4 h-4 text-primary" />
        <h4 className="text-xs uppercase tracking-widest text-primary">Price Elasticity Range</h4>
      </div>
      
      <p className="text-sm text-muted-foreground mb-3">
        {narrative || 'Negotiation bandwidth analysis based on market conditions.'}
      </p>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] text-muted-foreground uppercase mb-1">Elasticity Strength</p>
          <p className={`font-medium ${strengthColors[elasticityStrength] || 'text-muted-foreground'}`}>
            {elasticityStrength || 'N/A'}
          </p>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground uppercase mb-1">Model Confidence</p>
          <p className="font-medium">{modelConfidence || 0}%</p>
        </div>
      </div>
      
      <div className="mt-3 pt-2 border-t border-primary/20 text-[10px] text-muted-foreground flex items-center gap-1">
        <Info className="w-3 h-3" />
        <span>Visible to verified buyers only</span>
      </div>
    </div>
  );
};

/**
 * DataIntegrityFooter - Footer for Deal Card showing data integrity notice
 */
export const DataIntegrityFooter = ({ integrityData }) => {
  if (!integrityData) return null;
  
  // Helper to extract value from credibility-wrapped fields
  const getValue = (field) => {
    if (field && typeof field === 'object' && 'value' in field) {
      return field.value;
    }
    return field;
  };
  
  const sources = getValue(integrityData.primary_sources);
  const modelInputs = getValue(integrityData.model_inputs);
  const confidenceLevel = getValue(integrityData.confidence_level);
  const generatedAt = getValue(integrityData.generated_at);
  
  return (
    <div className="p-4 rounded bg-secondary/10 border border-white/5">
      <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
        Data Integrity Notice
      </h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-[10px] text-muted-foreground uppercase mb-1">Primary Sources</p>
          <p className="text-xs">{Array.isArray(sources) ? sources.join(', ') : (sources || 'DLD')}</p>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground uppercase mb-1">Model Inputs</p>
          <p className="text-xs">{modelInputs || 0} Verified Transactions</p>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground uppercase mb-1">Confidence Level</p>
          <Badge variant="outline" className={`text-[10px] ${
            confidenceLevel === 'HIGH' ? 'border-green-600/30 text-green-400' :
            'border-gold-600/30 text-gold-400'
          }`}>
            {confidenceLevel || 'MODERATE'}
          </Badge>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground uppercase mb-1">Generated</p>
          <p className="text-xs">{generatedAt ? new Date(generatedAt).toLocaleDateString() : 'N/A'}</p>
        </div>
      </div>
    </div>
  );
};

export default {
  DataCredibilityBadge,
  CredibleMetric,
  ZoneConfidenceCard,
  PriceElasticityCard,
  DataIntegrityFooter
};
